import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import {
  Calendar as CalendarIcon,
  Package,
  Ruler,
  ArrowRight,
  ArrowLeft,
  Wrench,
  FileText,
  Search,
  Info,
  CheckCircle2,
  Pause,
  Play,
  CheckCircle,
  Download,
  FileSearch,
} from "lucide-react";
import { format } from "date-fns";

import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Calendar } from "@shared/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import { cn } from "@shared/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Textarea } from "@shared/ui/textarea";
import { Input } from "@shared/ui/input";
import { RadioGroup, RadioGroupItem } from "@shared/ui/radio-group";
import { Label } from "@shared/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/ui/table";
import { Badge } from "@shared/ui/badge";
import { Tabs } from "@shared/ui/tabs";
import { useToast } from "@shared/hooks/use-toast";
import ApplicationHeader from "./ApplicationHeader";
import MainLayout from "./MainLayout";
import { useWorkflow } from "./WorkflowContext";
import WorkflowProgress from "./WorkflowProgress";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  useServiceOrder,
  useUpdateMeterInstallation,
  useConsumerDetails,
  useSearchMeter,
  useUpdateConsumerInstallationStatus,
  useServiceOrderConsumer,
  useUpdateStatus,
  useDownloadReport,
} from "../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import { queryClient } from "@shared/api/queries/queryClient";
import { ActionButton } from "@features/cx/shared/types/consumerRequest";

// Enhanced Form array interface
interface UtilityServiceForm {
  utilityService: string;
  deviceNumber: string;
  latestReading: string;
  readingDate: string;
  selectedConsumer?: any;
  selectedMeter?: any;
}

interface FormData {
  services: UtilityServiceForm[];
}

const InstallationPage = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const {
    application,
    workflowSteps,
    moveToNextStep,
    updateApplicationStatus,
  } = useWorkflow();
  const navigate = useNavigate();
  const location = useLocation();
  const { consumerId } = useParams<{ consumerId: string }>();
  const { toast } = useToast();
console.log('application consumer',application)
  const { startTracking, logTimeUntilStep, stopTracking } =
    useFormCompletionTracker("Service Connection Workflow");
  startTracking();
  // State management
  const [installationType, setInstallationType] = useState<
    "schedule" | "assign"
  >("schedule");
  const [showServiceOrderDetails, setShowServiceOrderDetails] =
    useState<boolean>(false);
  const [serviceOrderDetails, setServiceOrderDetails] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [latestReading, setLatestReading] = useState<string>("");
  const [readingDate, setReadingDate] = useState<Date>();
  const [deviceNumber, setDeviceNumber] = useState<string>("");
  const [currentSearchValue, setCurrentSearchValue] = useState<string>("");
  const [activeUtilityService, setActiveUtilityService] = useState<string>("");
  const [activeUtilityTab, setActiveUtilityTab] = useState<string>("");
  const updateStatus = useUpdateStatus(Number(consumerId));
  
  // Report functionality hooks
  const downloadReport = useDownloadReport();
  const viewReport = useDownloadReport();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const currentTab = searchParams.get("tab");

    if (currentTab && currentTab !== activeUtilityTab) {
      setActiveUtilityTab(currentTab);
      setLatestReading("");
      setReadingDate(undefined);
    }
  }, [location.search, activeUtilityTab]);

  // Form array setup
  const { control, handleSubmit, watch, setValue } = useForm<FormData>({
    defaultValues: {
      services: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "services",
  });

  const watchedServices = watch("services");
  const [activeSearchIndex, setActiveSearchIndex] = useState<number | null>(
    null
  );
  const [currentMeterSearchData, setCurrentMeterSearchData] =
    useState<any>(null);

  // Enhanced state for meter details
  const [selectedMeterDetails, setSelectedMeterDetails] = useState<
    Record<number, any>
  >({});
  const [loadingMeterDetails, setLoadingMeterDetails] = useState<
    Record<number, boolean>
  >({});

  // Dynamic utility tabs state
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>(
    {}
  );
  const [selectedMeters, setSelectedMeters] = useState<Record<string, any>>({});

  // Access acceptance data from navigation state
  const acceptanceData = location.state?.acceptanceData;

  // Initialize the update consumer service mutation
  const updateConsumerServiceMutation = useUpdateMeterInstallation();

  // Initialize the update consumer installation status mutation
  const updateConsumerInstallationStatusMutation =
    useUpdateConsumerInstallationStatus();

  // Fetch consumer details to get the required data for payload
  const { data: consumerDetailsData, isLoading: isLoadingConsumerDetails } =
    useConsumerDetails({
      remote_utility_id: remoteUtilityId,
      consumer_id: consumerId,
      is_detail_info: true,
    });

  // Fetch service orders using the hook
  const {
    data: serviceOrdersResponse,
    isLoading: isLoadingServiceOrders,
    error: serviceOrdersError,
  } = useServiceOrder({
    remote_utility_id: remoteUtilityId,
    search_data: searchTerm,
  });

  // Fetch service order details for the consumer
  const {
    data: serviceOrderConsumerData,
    isLoading: isLoadingServiceOrderConsumer,
  } = useServiceOrderConsumer({
    remote_utility_id: remoteUtilityId.toString(),
    consumer_id: consumerId || "",
    tab_name: "Installation_Inprogress",
  });

  console.log('serviceOrderConsumerData',serviceOrderConsumerData)
  // Extract report ID for report functionality
  const reportId =
    serviceOrderConsumerData?.result?.id || serviceOrderConsumerData?.result?.jobCardNo;

  // Get premise code from consumer details
  const premiseCode = useMemo(() => {
    return consumerDetailsData?.result?.addressData?.service?.PREMISE || "";
  }, [consumerDetailsData]);

  // Get meter mapping data from consumer details
  const meterMappingData = useMemo(() => {
    return consumerDetailsData?.result?.consumerMappingData || [];
  }, [consumerDetailsData]);

  // Get utility services from meter mapping data
  const utilityServices = useMemo(() => {
    const services = meterMappingData?.map((mapping: any) => mapping.utilityService) || [];
    return services;
  }, [meterMappingData]);

  // Get only unassigned utility services (those without meterDetails)
  const unassignedUtilityServices = useMemo(() => {
    const unassigned = meterMappingData?.filter((mapping: any) => !mapping.meterDetails) || [];
    return unassigned.map((mapping: any) => mapping.utilityService);
  }, [meterMappingData]);

  // Check if any utility service is missing meter details
  const hasUnassignedMeters = useMemo(() => {
    return meterMappingData?.some((mapping: any) => !mapping.meterDetails) || false;
  }, [meterMappingData]);

  // Initialize form array when consumerDetailsData is available
  useEffect(() => {
    if (consumerDetailsData?.result?.consumerMappingData) {
      const initialServices =
        consumerDetailsData.result.consumerMappingData.map((product: any) => ({
          utilityService: product.utilityService,
          deviceNumber: "",
          latestReading: "",
          readingDate: "",
          selectedConsumer: null,
          selectedMeter: null,
        }));

      // Clear existing and append new services
      remove();
      initialServices.forEach((service: UtilityServiceForm) => append(service));
    }
  }, [consumerDetailsData, append, remove]);

  useEffect(() => {
    if (
      serviceOrderConsumerData?.result?.statusDisplay === "Approved" ||
      serviceOrderDetails?.status == "Approved"
    ) {
      updateApplicationStatus("activation");
      moveToNextStep();
    }
  }, [
    serviceOrderConsumerData?.result?.statusDisplay,
    serviceOrderDetails?.status,
  ]);

  // Initialize search queries when utility services are loaded
  useEffect(() => {
    if (utilityServices.length > 0) {
      const initialQueries: Record<string, string> = {};
      utilityServices.forEach((service: string) => {
        initialQueries[service] = "";
      });
      setSearchQueries(initialQueries);
    }
  }, [utilityServices]);

  const params = {
    remote_utility_id: remoteUtilityId,
    premise: premiseCode,
    device_number: currentSearchValue,
    utility_service: activeUtilityService,
  };

  const { data: meterSearchData } = useSearchMeter(params);

  // Enhanced function to fetch meter details after selection
  const fetchMeterDetails = async (
    deviceNumber: string,
    utilityService: string,
    index: number
  ) => {
    try {
      setLoadingMeterDetails((prev) => ({ ...prev, [index]: true }));
      if (currentMeterSearchData?.result) {
        const selectedMeterData = currentMeterSearchData.result.find(
          (meter: any) => meter.deviceNo === deviceNumber
        );

        if (selectedMeterData) {
          // Update the selected meter details state
          setSelectedMeterDetails((prev) => ({
            ...prev,
            [index]: selectedMeterData,
          }));

          // Update form field with selected meter data
          setValue(`services.${index}.selectedMeter`, selectedMeterData);

          toast({
            title: "Meter Details Loaded",
            description: `Meter ID: ${selectedMeterData.id} for device ${deviceNumber}`,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching meter details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch meter details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingMeterDetails((prev) => ({ ...prev, [index]: false }));
    }
  };

  // Handle device number search for form array
  const handleDeviceNumberChange = async (value: string, index: number) => {
    setValue(`services.${index}.deviceNumber`, value);
    setCurrentSearchValue(value);

    // Set the active utility service for the current index
    const currentService =
      watchedServices[index]?.utilityService || fields[index]?.utilityService;
    setActiveUtilityService(currentService);

    if (value.length >= 3) {
      setActiveSearchIndex(index);
      setCurrentMeterSearchData(meterSearchData);
    } else {
      setActiveSearchIndex(null);
      setCurrentMeterSearchData(null);
      // Clear selected meter details when search is cleared
      setSelectedMeterDetails((prev) => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
      setValue(`services.${index}.selectedMeter`, null);
    }
  };

  // Enhanced consumer selection handler
  const handleConsumerSelect = async (meter: any, index: number) => {
    const deviceNumber = meter.deviceNo || "";
    const utilityService =
      watchedServices[index]?.utilityService || fields[index]?.utilityService;

    setValue(`services.${index}.deviceNumber`, deviceNumber);
    setValue(`services.${index}.selectedConsumer`, meter);

    // Clear search UI states but keep the device number and utility service for API call
    setActiveSearchIndex(null);
    setCurrentMeterSearchData(null);

    // Fetch detailed meter information after selection with the selected device number and utility service
    if (deviceNumber && utilityService) {
      await fetchMeterDetails(deviceNumber, utilityService, index);
    }

    // Clear search states after API call
    setCurrentSearchValue("");
    setActiveUtilityService("");
  };

  // Report functionality handlers
  const handleViewReport = async () => {
    if (!reportId) {
      toast({
        title: "No report available",
        description: "Report ID not found for this inspection.",
        variant: "destructive",
      });
      return;
    }

    try {
      const blob = await viewReport.mutateAsync({
        remote_utility_id: remoteUtilityId,
        report_id: reportId,
      });

      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      toast({
        title: "Error viewing report",
        description: "Failed to open the inspection report.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadReport = async () => {
    if (!reportId) {
      toast({
        title: "No report available",
        description: "Report ID not found for this inspection.",
        variant: "destructive",
      });
      return;
    }

    try {
      await downloadReport.mutateAsync({
        remote_utility_id: remoteUtilityId,
        report_id: reportId,
      });

      toast({
        title: "Download started",
        description: "The inspection report is being downloaded.",
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Failed to download the inspection report.",
        variant: "destructive",
      });
    }
  };

  // Create multiple useSearchMeter hooks for each utility service
  const meterQueries = useMemo(() => {
    return utilityServices.reduce((acc: any, service: string) => {
      acc[service] = {
        remote_utility_id: remoteUtilityId,
        utility_service: service,
        premise: premiseCode,
        device_number: deviceNumber,
      };
      return acc;
    }, {});
  }, [utilityServices, remoteUtilityId, premiseCode]);

  // Use conditional hooks for each utility service
  const waterMetersQuery = useSearchMeter(
    utilityServices.includes("Water") ? meterQueries["Water"] : null
  );

  const wasteWaterMetersQuery = useSearchMeter(
    utilityServices.includes("Waste Water") ? meterQueries["Waste Water"] : null
  );

  const electricMetersQuery = useSearchMeter(
    utilityServices.includes("Electric") ? meterQueries["Electric"] : null
  );

  const gasMetersQuery = useSearchMeter(
    utilityServices.includes("Gas") ? meterQueries["Gas"] : null
  );

  // Create a mapping of utility services to their respective query results
  const meterQueriesResults = useMemo(() => {
    const results: Record<string, any> = {};
    if (utilityServices.includes("Water")) {
      results["Water"] = waterMetersQuery;
    }
    if (utilityServices.includes("Waste Water")) {
      results["Waste Water"] = wasteWaterMetersQuery;
    }
    if (utilityServices.includes("Electric")) {
      results["Electric"] = electricMetersQuery;
    }
    if (utilityServices.includes("Gas")) {
      results["Gas"] = gasMetersQuery;
    }
    return results;
  }, [
    utilityServices,
    waterMetersQuery,
    wasteWaterMetersQuery,
    electricMetersQuery,
    gasMetersQuery,
  ]);

  // Transform API response to the format expected by the form
  const serviceOrderOptions = useMemo(() => {
    if (!serviceOrdersResponse?.result) {
      return [];
    }

    const options = serviceOrdersResponse.result.map((order: any) => ({
      label: ` ${order.name}`,
      value: order.id.toString(),
      data: {
        id: order.id,
        service_no: order.service_no,
        name: order.name,
        sop_type: order.sop_type,
        sop_subtype: order.sop_subtype,
        short_name: order.short_name,
        est_total_cost: order.est_total_cost,
        est_effort: order.est_effort,
      },
    }));

    return options;
  }, [serviceOrdersResponse]);

  // Filter meters based on search query for active tab
  const getFilteredMeters = (utilityService: string) => {
    const queryResult = meterQueriesResults[utilityService];
    if (!queryResult?.data?.result) return [];

    const searchQuery = searchQueries[utilityService] || "";
    return queryResult.data.result.filter(
      (meter: any) =>
        meter.meterNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        meter.deviceNo?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Handle search query change for specific utility
  const handleSearchQueryChange = (utilityService: string, query: string) => {
    setSearchQueries((prev) => ({
      ...prev,
      [utilityService]: query,
    }));
  };

  // Enhanced meter selection for tabs approach
  const handleMeterSelect = async (utilityService: string, meter: any) => {
    setSelectedMeters((prev) => ({
      ...prev,
      [utilityService]: meter,
    }));

    // Fetch detailed meter information for tabs approach
    if (meter.deviceNo && utilityService) {
      try {
        const detailParams = {
          remote_utility_id: remoteUtilityId,
          premise: premiseCode,
          device_number: searchQueries[utilityService],
          utility_service: utilityService,
        };

        const { data: meterDetailsResponse } = useSearchMeter(detailParams);

        if (
          meterDetailsResponse?.result &&
          meterDetailsResponse.result.length > 0
        ) {
          const selectedMeterData = meterDetailsResponse.result[0];

          // Update selected meters with detailed information
          setSelectedMeters((prev) => ({
            ...prev,
            [utilityService]: {
              ...meter,
              detailedInfo: selectedMeterData,
              meterId: selectedMeterData.id,
            },
          }));

          toast({
            title: "Meter Selected",
            description: `${utilityService} meter selected - ID: ${selectedMeterData.id}`,
          });
        }
      } catch (error) {
        console.error("Error fetching meter details:", error);
      }
    }
  };

  // Check if at least one meter is selected
  const isAtLeastOneMeterSelected = () => {
    return Object.keys(selectedMeters).length > 0;
  };

  // Custom tabs configuration for utility services
  const utilityTabsConfig = useMemo(() => {
    const config: Record<string, any> = {};

    unassignedUtilityServices.forEach((service: string) => {
      const queryResult = meterQueriesResults[service];
      const filteredMeters = getFilteredMeters(service);
      const isLoading = queryResult?.isLoading;
      const hasError = queryResult?.error;
      config[service] = {
        label: `${service === "Waste Water" ? "Waste Water" : service} Utility`,
        icon:
          service === "Water" ? (
            <Package className="h-4 w-4" />
          ) : service === "Electric" ? (
            <Ruler className="h-4 w-4" />
          ) : (
            <Wrench className="h-4 w-4" />
          ),
        count: filteredMeters.length,
        countClassName: selectedMeters[service] ? "bg-green-500" : undefined,
        loading: isLoading,
        component: (
          <div className="space-y-4">
            {/* Search Section */}
            <div>
              <h3 className="text-sm font-medium mb-2">
                Search {service} Meters
              </h3>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={`Search by ${service.toLowerCase()} meter number or device number...`}
                  className="pl-8"
                  value={searchQueries[service] || ""}
                  onChange={(e) =>
                    handleSearchQueryChange(service, e.target.value)
                  }
                />
              </div>
            </div>

            {/* Meters Table */}
            <div>
              <h3 className="text-sm font-medium mb-4">
                Available {service} Meters
              </h3>
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Meter No</TableHead>
                      <TableHead>Device No</TableHead>
                      <TableHead>Physical Status</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center py-4 text-muted-foreground"
                        >
                          Loading {service.toLowerCase()} meters...
                        </TableCell>
                      </TableRow>
                    ) : filteredMeters.length > 0 ? (
                      filteredMeters.map((meter: any) => {
                        return (
                          <TableRow
                            key={meter.id}
                            className={cn(
                              selectedMeters[service]?.id === meter.id
                                ? "bg-blue-50"
                                : ""
                            )}
                          >
                            <TableCell>{meter.meterNumber}</TableCell>
                            <TableCell>{meter.deviceNo}</TableCell>
                            <TableCell>{meter.physicalStatusDisplay}</TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className="text-green-800 bg-green-100"
                              >
                                {meter.statusDisplay}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleMeterSelect(service, meter)
                                }
                              >
                                {selectedMeters[service]?.id === meter.id
                                  ? "Selected"
                                  : "Select"}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center py-4 text-muted-foreground"
                        >
                          {hasError
                            ? "Error loading meters"
                            : searchQueries[service]
                            ? "No meters match your search"
                            : "No meters available"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Selected Meter Details */}
            {selectedMeters[service] && (
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-sm font-medium">
                  Selected {service} Meter Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Meter Number
                    </p>
                    <p className="font-medium">
                      {selectedMeters[service].meterNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Device Number
                    </p>
                    <p className="font-medium">
                      {selectedMeters[service].deviceNo}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Meter ID</p>
                    <p className="font-medium text-green-600">
                      {selectedMeters[service].meterId ||
                        selectedMeters[service].id}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <p className="font-medium">
                      {selectedMeters[service].statusDisplay}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ),
        loadingComponent: (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3"></div>
              <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        ),
      };
    });

    return config;
  }, [unassignedUtilityServices, meterQueriesResults, searchQueries, selectedMeters]);

  // Define form fields for scheduling installation with dynamic service orders
  const scheduleInstallationFields: FormField[] = useMemo(() => {
    return [
      {
        name: "selectedServiceOrder",
        label: "Select Service Order",
        type: "select",
        required: true,
        options: serviceOrderOptions,
        placeholder: isLoadingServiceOrders
          ? "Loading service orders..."
          : "Select service order",
        disabled: isLoadingServiceOrders,
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "text-sm font-medium mb-2 block",
          select:
            "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-workflow-blue focus:border-workflow-blue",
          error: "text-red-600 text-sm mt-1",
        },
      },
      {
        name: "installationDate",
        label: "Select Installation Date",
        type: "datepicker",
        required: true,
        placeholder: "Select date",
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "text-sm font-medium mb-2 block",
          input:
            "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-workflow-blue focus:border-workflow-blue",
          error: "text-red-600 text-sm mt-1",
        },
      },
      {
        name: "specialInstructions",
        label: "Special Instructions",
        type: "textarea",
        required: false,
        placeholder: "Add any special instructions for installation...",
        rows: 4,
        maxLength: 500,
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "text-sm font-medium mb-2 block",
          textarea:
            "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-workflow-blue focus:border-workflow-blue resize-none",
          error: "text-red-600 text-sm mt-1",
        },
      },
    ];
  }, [serviceOrderOptions, isLoadingServiceOrders]);

  // Create form using FormService
  const scheduleForm = FormService.createForm({
    fields: scheduleInstallationFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Debug logging for form and render states
  useEffect(() => {}, [
    installationType,
    showServiceOrderDetails,
    utilityServices,
    serviceOrderOptions,
    isLoadingServiceOrders,
    scheduleForm.formState,
  ]);

  // Handle service orders loading error
  useEffect(() => {
    if (serviceOrdersError) {
      toast({
        title: "Error Loading Service Orders",
        description: "Failed to load service orders. Please try again.",
        variant: "destructive",
      });
    }
  }, [serviceOrdersError, toast]);

  // Handle meter queries errors
  useEffect(() => {
    utilityServices.forEach((service) => {
      const queryResult = meterQueriesResults[service];
      if (queryResult?.error) {
        toast({
          title: `Error Loading ${service} Meters`,
          description: `Failed to load ${service.toLowerCase()} meters. Please try again.`,
          variant: "destructive",
        });
      }
    });
  }, [meterQueriesResults, utilityServices, toast]);

  useEffect(() => {}, [acceptanceData, consumerDetailsData, application]);

  if (!application) {
    return <div>Loading...</div>;
  }

  // Handle schedule form submission
const handleScheduleFormSubmit = async (formData: any) => {
  const { selectedServiceOrder, installationDate, specialInstructions } = formData;

  try {
    const selectedOrder = serviceOrderOptions.find(
      (so) => so.value === selectedServiceOrder
    );

    if (!selectedOrder) {
      toast({
        title: "Error",
        description: "Selected service order not found.",
        variant: "destructive",
      });
      return;
    }

    const formattedDate = format(
      new Date(installationDate),
      "yyyy-MM-dd HH:mm:ss.SSS xxxx"
    );

    // ✅ Fix: use .result for the actual data
    const consumerData =
      consumerDetailsData?.result || acceptanceData?.result || {};

    console.log("✅ consumerData at installation", consumerData);

    // ✅ Safely compute consumerFullName
    const consumerFullName = `${consumerData?.firstName ?? ""} ${consumerData?.lastName ?? ""}`.trim();

    // ✅ Build payload
    const payload: any = {
      is_workorder: 1,
      remote_utility_id: remoteUtilityId,
      sop: parseInt(selectedServiceOrder),
      request_account_no:
        consumerData.consumer_no ||
        consumerData.account_no ||
        consumerData.consumer_number ||
        application?.id ||
        "",
      request_id:
        consumerData.id ||
        consumerData.consumer_id ||
        consumerData.request_id ||
        parseInt(consumerId || "0"),
      request_username:
        consumerFullName ||
        consumerData.consumer_name ||
        consumerData.customer_name ||
        application?.customerName ||
        "",
      mobile_number:
        consumerData.contact_number ||
        consumerData.mobile_number ||
        consumerData.phone ||
        application?.customerPhone ||
        "",
      area: application?.addressData?.service?.AREA || "",
      sub_area: application?.addressData?.service?.SUBAREA || "",
      premise: application?.addressData?.service?.PREMISE || "",
      request_availability_date: formattedDate,
      remark: specialInstructions || "",
    };

    console.log("🧾 installation payload", payload);

   
    await updateConsumerServiceMutation.mutateAsync(
      { consumerId, payload },
      { onSuccess: () => logTimeUntilStep("Installation") }
    );

    setServiceOrderDetails({
      ...selectedOrder.data,
      name: selectedOrder.label,
      installationDate,
      specialInstructions,
      status: "Scheduled",
    });
    setShowServiceOrderDetails(true);

    toast({
      title: "Installation scheduled successfully",
      description: `Meter installation scheduled for ${format(
        new Date(installationDate),
        "PPP"
      )} for service order: ${selectedOrder.label}.`,
    });
  } catch (error) {
    toast({
      title: "Error scheduling installation",
      description: "Failed to schedule the installation. Please try again.",
      variant: "destructive",
    });
  }
};


  const handleAssignMeters = async () => {
    if (!isAtLeastOneMeterSelected()) {
      toast({
        title: "No meters selected",
        description: "Please select at least one meter to proceed.",
        variant: "destructive",
      });
      return;
    }

    // Check if all unassigned utility services have meter details
    const unassignedServicesWithoutMeters = unassignedUtilityServices.filter(
      (service) => !selectedMeters[service]
    );

    if (unassignedServicesWithoutMeters.length > 0) {
      toast({
        title: "Incomplete meter assignment",
        description: `Please select meters for all utility services: ${unassignedServicesWithoutMeters.join(", ")}`,
        variant: "destructive",
      });
      return;
    }

    if (!latestReading || !readingDate) {
      toast({
        title: "Incomplete information",
        description: "Please provide meter reading information.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Format the date for API (ISO format)
      const formattedReadingDate = readingDate.toISOString();

      // Build utility service meter data payload exactly as required - only selected meters
      const utilityServiceMeterData: Record<string, any> = {};

      Object.keys(selectedMeters).forEach((service) => {
        const selectedMeter = selectedMeters[service];
        if (selectedMeter && (selectedMeter.meterId || selectedMeter.id)) {
          utilityServiceMeterData[service] = {
            meter_id: selectedMeter.meterId || selectedMeter.id,
            current_reading: latestReading,
            current_reading_date: formattedReadingDate,
          };
        }
      });

      const payload = {
        utility_service_meter_data: utilityServiceMeterData,
      };

      // Make API call to update consumer installation status
      await updateConsumerInstallationStatusMutation.mutateAsync({
        consumerId: consumerId!,
        payload: payload,
      });

      toast({
        title: "Meters assigned successfully",
        description:
          "Meters have been assigned to this connection and installation status updated.",
      });

      // Check if all unassigned utility services have meter details before navigation
      const unassignedServicesWithoutMeters = unassignedUtilityServices.filter(
        (service) => !selectedMeters[service]
      );

      if (unassignedServicesWithoutMeters.length > 0) {
        toast({
          title: "Incomplete meter assignment",
          description: `Please select meters for all utility services: ${unassignedServicesWithoutMeters.join(", ")}`,
          variant: "destructive",
        });
        return;
      }

      navigate(`/cx/consumer-onboarding/workflow/activation/${consumerId}`);
    } catch (error) {
      console.error("Error assigning meters:", error);
      toast({
        title: "Error assigning meters",
        description:
          "Failed to assign meters and update installation status. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle form array submission for Continue to Activation
  const handleFormArraySubmit = async () => {
    const formData = watchedServices;

    // Validate that at least one service has complete data with selected meter
    const completeServices = formData.filter(
      (service) =>
        service.deviceNumber &&
        service.latestReading &&
        service.readingDate &&
        service.selectedMeter // Ensure meter is selected
    );

    if (completeServices.length === 0) {
      toast({
        title: "Incomplete information",
        description:
          "Please provide device number, reading, date and select a meter for at least one utility service.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Build utility service meter data payload exactly as required
      const utilityServiceMeterData: Record<string, any> = {};

      completeServices.forEach((service) => {
        // Only include services that have selected meter details
        if (service.selectedMeter && service.selectedMeter.id) {
          utilityServiceMeterData[service.utilityService] = {
            meter_id: service.selectedMeter.id,
            current_reading: service.latestReading,
            current_reading_date: new Date(service.readingDate).toISOString(),
          };
        }
      });

      // Final validation to ensure we have at least one complete service with meter
      if (Object.keys(utilityServiceMeterData).length === 0) {
        toast({
          title: "No meters selected",
          description:
            "Please select meters for the utility services before proceeding.",
          variant: "destructive",
        });
        return;
      }

      const payload = {
        utility_service_meter_data: utilityServiceMeterData,
      };
      // Make API call to update consumer installation status
      await updateConsumerInstallationStatusMutation.mutateAsync({
        consumerId: consumerId!,
        payload: payload,
      });

      toast({
        title: "Installation data updated successfully",
        description:
          "Utility service data has been updated and ready for activation.",
      });

      navigate(`/cx/consumer-onboarding/workflow/activation/${consumerId}/INSTALLATION INPROGRESS`);
    } catch (error) {
      console.error("Error updating installation data:", error);
      toast({
        title: "Error updating installation data",
        description: "Failed to update installation data. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleBack = () => {
    navigate(`/cx/consumer-onboarding/workflow/approval/${consumerId}`);
  };

  const renderScheduleCustomFooter = () => (
    <div className="flex justify-between items-center pt-6 border-t border-gray-200">
      <Button variant="outline" onClick={handleBack} type="button">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>
      <Button
        type="submit"
        disabled={
          isLoadingServiceOrders ||
          updateConsumerServiceMutation.isPending ||
          application.progressStatusDisplay === "Hold"
        }
        onClick={() => {}}
      >
        {updateConsumerServiceMutation.isPending
          ? "Scheduling..."
          : "Schedule Installation"}
        <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
    </div>
  );

  const isEmptyObject = (obj) =>
    obj && Object.keys(obj).length === 0 && obj.constructor === Object;

  const handleAcknowledge = () => {
    // update the status in the database
    const acknowledge_date = new Date().toISOString();
    updateStatus.mutate({
      remote_utility_id: remoteUtilityId,
      progress_status: "IN_PROGRESS",
      acknowledge_date,
    });
    toast({
      title: "Application acknowledged",
      description:
        "The application has been acknowledged and is now in progress.",
    });
  };

  const handlePutOnHold = () => {
    try {
      updateStatus.mutate({
        remote_utility_id: remoteUtilityId,
        progress_status: "HOLD",
      });

      toast({
        title: "Application on hold",
        description: "The application has been put on hold.",
      });
    } catch (error) {
      console.error("Error putting application on hold:", error);
      toast({
        title: "Error",
        description: "Failed to put application on hold. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleResume = () => {
    try {
      updateStatus.mutate({
        remote_utility_id: remoteUtilityId,
        progress_status: "IN_PROGRESS",
      });

      toast({
        title: "Application resumed",
        description: "The application has been resumed and is now in progress.",
      });
    } catch (error) {
      console.error("Error resuming application:", error);
      toast({
        title: "Error",
        description: "Failed to resume application. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Define action buttons
  const actionButtons: ActionButton[] = [
    {
      id: "acknowledge",
      label: "Acknowledge",
      icon: CheckCircle,
      variant: "default",
      onClick: handleAcknowledge,
      condition: (status) =>
        application.progressStatusDisplay === "Acknowledge Pending",
    },
    {
      id: "hold",
      label: "Put on Hold",
      icon: Pause,
      variant: "outline",
      onClick: handlePutOnHold,
      condition: (status) =>
        application.progressStatusDisplay === "In Progress",
    },
    {
      id: "resume",
      label: "Resume",
      icon: Play,
      variant: "default",
      onClick: handleResume,
      condition: (status) => application.progressStatusDisplay === "Hold",
    },
  ];

  const relevantActions = actionButtons.filter(
    (action) =>
      !action.condition || action.condition(application.progressStatusDisplay)
  );

  function getStatusClasses(status) {
    switch (status) {
      case "Acknowledge Pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "In Progress":
        return "bg-green-100 text-green-800 border-green-300";
      case "Hold":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  }

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          Service Connection Workflow{" "}
          <Badge
            className={getStatusClasses(application.progressStatusDisplay)}
          >
            {application.progressStatusDisplay}
          </Badge>
        </h1>
        <div className="flex gap-2">
          {relevantActions.map((action) => (
            <Button
              key={action.id}
              size="sm"
              variant={action.variant}
              onClick={action.onClick}
            >
              <action.icon className="h-4 w-4 mr-2" />
              {action.label}
            </Button>
          ))}
        </div>
      </div>

      <ApplicationHeader
        remote_utility_id={remoteUtilityId}
        is_detail_info={1}
      />

      <WorkflowProgress
        steps={workflowSteps}
        currentStepId={application.currentStep}
        status={serviceOrderConsumerData?.result?.statusDisplay}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div>
                <div className="flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-workflow-blue" />
                  <CardTitle>Meter Installation</CardTitle>
                </div>
                <CardDescription className="ml-7">
                  Schedule a new meter installation or assign an existing meter
                </CardDescription>
              </div>
            </CardHeader>

            <CardContent className="mt-4">
              {!showServiceOrderDetails &&
                hasUnassignedMeters &&
                (!serviceOrderConsumerData?.result || Object.keys(serviceOrderConsumerData.result).length === 0) && (
                  <>
                    {/* Information message explaining why form is shown */}
                    {hasUnassignedMeters && meterMappingData.length > 0 && (
                      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">
                              Partial Meter Assignment Required
                            </h3>
                            <div className="mt-2 text-sm text-blue-700">
                              <p>
                                Some utility services already have meters assigned, but there are still services that need meter installation. 
                                Please complete the meter assignment for the remaining services below.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                  <div className="mb-6">
                    <h3 className="text-sm font-medium mb-2">
                      Installation Type
                    </h3>
                    <RadioGroup
                      value={installationType}
                      onValueChange={(value) => {
                        setInstallationType(value as "schedule" | "assign");
                        setShowServiceOrderDetails(false);
                      }}
                      className="flex flex-col md:flex-row gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="schedule" id="schedule" />
                        <Label htmlFor="schedule">
                          Schedule New Installation
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="assign" id="assign" />
                        <Label htmlFor="assign">Assign Existing Meter</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </>
                )}

              {!showServiceOrderDetails &&
              hasUnassignedMeters &&
              (!serviceOrderConsumerData?.result || Object.keys(serviceOrderConsumerData.result).length === 0) &&
              installationType === "schedule" ? (
                <div className="space-y-6">
                  {/* Dynamic Form Implementation */}
                  <DynamicForm
                    fields={scheduleInstallationFields}
                    form={scheduleForm}
                    onSubmit={handleScheduleFormSubmit}
                    renderCustomFooter={renderScheduleCustomFooter}
                    config={{
                      layout: "vertical",
                      spacing: "medium",
                      showResetButton: false,
                      classes: {
                        form: "space-y-6",
                        fieldContainer: "w-full",
                        buttonContainer: "hidden", // Hide default buttons since we're using custom footer
                      },
                    }}
                  />
                </div>
              ) : !showServiceOrderDetails &&
                hasUnassignedMeters &&
                (!serviceOrderConsumerData?.result || Object.keys(serviceOrderConsumerData.result).length === 0) &&
                installationType === "assign" &&
                unassignedUtilityServices.length > 0 ? (
                <div className="space-y-6">
                  <Tabs
                    defaultValue={unassignedUtilityServices[0]}
                    tabComponents={utilityTabsConfig}
                    tabsListClassName={`grid w-full grid-cols-${unassignedUtilityServices.length}`}
                    idPrefix="utility-meters"
                    level={0}
                    defaultLoadingComponent={
                      <div className="space-y-4">
                        <div className="h-4 bg-gray-200 rounded animate-pulse w-1/4"></div>
                        <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    }
                  />

                  {/* Meter reading section - only show if at least one meter is selected */}
                  {Object.keys(selectedMeters).length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 border-t mt-6">
                      <div>
                        <Label htmlFor="latestReading" className="mb-2 block">
                          Latest Meter Reading
                        </Label>
                        <Input
                          id="latestReading"
                          placeholder="Enter current meter reading"
                          value={latestReading}
                          onChange={(e) => setLatestReading(e.target.value)}
                          type="number"
                        />
                      </div>
                      <div>
                        <Label htmlFor="readingDate" className="mb-2 block">
                          Reading Date
                        </Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              id="readingDate"
                              variant={"outline"}
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !readingDate && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {readingDate ? (
                                format(readingDate, "PPP")
                              ) : (
                                <span>Select date</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={readingDate}
                              onSelect={setReadingDate}
                              disabled={(date) => date > new Date()}
                              initialFocus
                              className="p-3 pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      type="button"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back
                    </Button>
                    <Button
                      onClick={handleAssignMeters}
                      disabled={
                        !(
                          application?.apiStatus?.toLowerCase() ===
                            "approved" ||
                          application?.apiStatus?.toLowerCase() ===
                            "installation inprogress"
                        ) || application?.progressStatusDisplay === "Hold"
                      }
                    >
                      Assign Meters
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              ) : !showServiceOrderDetails &&
                hasUnassignedMeters &&
                (!serviceOrderConsumerData?.result || Object.keys(serviceOrderConsumerData.result).length === 0) &&
                installationType === "assign" &&
                unassignedUtilityServices.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">
                    Loading utility services...
                  </p>
                </div>
              ) : (showServiceOrderDetails ||
                (serviceOrderConsumerData?.result && Object.keys(serviceOrderConsumerData.result).length > 0 && hasUnassignedMeters) ||
                (meterMappingData.length > 0 && !hasUnassignedMeters)) ? (
                <>
                {/* Meter Mapping Details - Show when available */}
                {meterMappingData.length > 0 && meterMappingData.some((mapping: any) => mapping.meterDetails) && (
                    <div className="bg-muted/50 rounded-lg p-4">
                      <div className="space-y-4">
                        {meterMappingData
                          .filter((mapping: any) => mapping.meterDetails)
                          .map((mapping: any, index: number) => (
                          <div key={mapping.consumer || index} className="border rounded-lg p-4 bg-white">
                            <div className="flex items-center gap-2 mb-3">
                              <Package className="h-5 w-5 text-workflow-blue" />
                              <h4 className="font-medium text-lg">
                                {mapping.utilityService} Meter
                              </h4>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm font-medium text-muted-foreground">Meter Number</p>
                                <p className="text-sm font-medium">{mapping.meterDetails.meterNumber}</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-muted-foreground">Device Number</p>
                                <p className="text-sm font-medium">{mapping.meterDetails.deviceNo}</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-muted-foreground">Meter ID</p>
                                <p className="text-sm font-medium text-green-600">{mapping.meterDetails.id}</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-muted-foreground">Meter Type</p>
                                <p className="text-sm font-medium">{mapping.meterDetails.meterTypeDisplay}</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-muted-foreground">Physical Status</p>
                                <Badge 
                                  variant="outline" 
                                  className={
                                    mapping.meterDetails.physicalStatusDisplay === "Installed" 
                                      ? "text-green-800 bg-green-100" 
                                      : "text-yellow-800 bg-yellow-100"
                                  }
                                >
                                  {mapping.meterDetails.physicalStatusDisplay}
                                </Badge>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-muted-foreground">Status</p>
                                <Badge 
                                  variant="outline" 
                                  className="text-blue-800 bg-blue-100"
                                >
                                  {mapping.meterDetails.statusDisplay}
                                </Badge>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-muted-foreground">Current Reading</p>
                                <p className="text-sm font-medium">{mapping.meterDetails.currentReading}</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-muted-foreground">Reading Date</p>
                                <p className="text-sm font-medium">{mapping.meterDetails.currentReadingDate}</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-muted-foreground">Route Name</p>
                                <p className="text-sm font-medium">{mapping.meterDetails.routeName}</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-muted-foreground">Cycle Name</p>
                                <p className="text-sm font-medium">{mapping.meterDetails.cycleName}</p>
                              </div>
                              {(mapping.meterDetails.lat && mapping.meterDetails.long) && (
                                <div className="col-span-2">
                                  <p className="text-sm font-medium text-muted-foreground">Location</p>
                                  <p className="text-sm font-medium">
                                    Lat: {mapping.meterDetails.lat}, Long: {mapping.meterDetails.long}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                )}

                {/* Service Order Details - Show when service order present AND some objects don't have meterDetails */}
                {serviceOrderConsumerData?.result && Object.keys(serviceOrderConsumerData.result).length > 0 && hasUnassignedMeters && (
                <div className="mt-2">
                  <h3 className="text-lg font-medium mb-4">
                    Service Order Details
                  </h3>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium">Service Order</p>
                        <p className="text-sm">
                          {serviceOrderConsumerData?.result?.sop?.name || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Status</p>
                        <Badge variant="outline" className="mt-1">
                          {serviceOrderConsumerData?.result?.statusDisplay ||
                            serviceOrderDetails?.status ||
                            "N/A"}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Job Card No</p>
                        <p className="text-sm">
                          {serviceOrderConsumerData?.result?.jobCardNo || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Created On</p>
                        <p className="text-sm">
                          {serviceOrderConsumerData?.result?.createdDate ||
                            (serviceOrderDetails?.createdDate
                              ? format(
                                  new Date(serviceOrderDetails.createdDate),
                                  "PPP"
                                )
                              : format(new Date(), "PPP"))}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Type</p>
                        <p className="text-sm">
                          {serviceOrderConsumerData?.result?.sopTypeDisplay ||
                            "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Subtype</p>
                        <p className="text-sm">
                          {serviceOrderConsumerData?.result
                            ?.sopSubtypeDisplay || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Assigned On</p>
                        <p className="text-sm">
                          {serviceOrderConsumerData?.result?.assignDate ||
                            (serviceOrderDetails?.assignedDate
                              ? format(
                                  new Date(serviceOrderDetails.assignedDate),
                                  "PPP"
                                )
                              : "Not assigned")}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Field Force</p>
                        <p className="text-sm">
                          {serviceOrderConsumerData?.result?.fieldForce?.name ||
                            serviceOrderDetails?.assigned_to ||
                            "Not assigned"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Expected End Date</p>
                        <p className="text-sm">
                          {serviceOrderConsumerData?.result?.expectedEndDate ||
                            (serviceOrderDetails?.installationDate
                              ? format(
                                  new Date(
                                    serviceOrderDetails.installationDate
                                  ),
                                  "PPP"
                                )
                              : "Not set")}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          Request Availability Date
                        </p>
                        <p className="text-sm">
                          {serviceOrderConsumerData?.result
                            ?.requestAvailabilityDate ||
                            (serviceOrderDetails?.installationDate
                              ? format(
                                  new Date(
                                    serviceOrderDetails.installationDate
                                  ),
                                  "PPP"
                                )
                              : "Not set")}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Account Number</p>
                        <p className="text-sm">
                          {serviceOrderConsumerData?.result?.requestAccountNo ||
                            "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Customer Name</p>
                        <p className="text-sm">
                          {serviceOrderConsumerData?.result?.requestUsername ||
                            "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Mobile Number</p>
                        <p className="text-sm">
                          {serviceOrderConsumerData?.result?.mobileNumber ||
                            "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Area</p>
                        <p className="text-sm">
                          {serviceOrderConsumerData?.result?.territoryData
                            ?.area || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Sub Area</p>
                        <p className="text-sm">
                          {serviceOrderConsumerData?.result?.territoryData
                            ?.subArea || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Premise</p>
                        <p className="text-sm">
                          {serviceOrderConsumerData?.result?.territoryData
                            ?.premise || "N/A"}
                        </p>
                      </div>
                      {(serviceOrderConsumerData?.result?.remark ||
                        serviceOrderDetails?.specialInstructions) && (
                        <div className="col-span-2">
                          <p className="text-sm font-medium">
                            Remarks / Special Instructions
                          </p>
                          <p className="text-sm">
                            {serviceOrderConsumerData?.result?.remark ||
                              serviceOrderDetails?.specialInstructions}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Report Action Buttons */}
                    {serviceOrderConsumerData?.result?.statusDisplay === "Approved" && (
                    <div className="flex gap-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={handleViewReport}
                        disabled={!reportId || viewReport.isPending}
                        className="flex items-center gap-2"
                      >
                        <FileSearch className="h-4 w-4" />
                        {viewReport.isPending
                          ? "Loading..."
                          : "View Report"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleDownloadReport}
                        disabled={!reportId || downloadReport.isPending}
                        className="flex items-center gap-2"
                      >
                        <Download className="h-4 w-4" />
                        {downloadReport.isPending
                          ? "Downloading..."
                          : "Download Report"}
                      </Button>
                    </div>
                    )}

                    {/* Enhanced Form Array Integration */}
                    {serviceOrderConsumerData?.result?.statusDisplay ===
                      "Approved" && (
                      <CardContent className="space-y-4 mt-4">
                        {fields.map((field, index) => (
                          <div
                            key={field.id}
                            className="border rounded-lg p-4 space-y-4"
                          >
                            <div>
                              <p className="text-sm font-medium">
                                Utility Service
                              </p>
                              <p className="text-sm mt-2 text-blue-600 font-medium">
                                {field.utilityService}
                              </p>
                            </div>

                            <div className="space-y-4">
                              <div>
                                <Label htmlFor={`search-${index}`}>
                                  Search Device Number
                                </Label>
                                <div className="relative">
                                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                  <Controller
                                    name={`services.${index}.deviceNumber`}
                                    control={control}
                                    render={({ field: inputField }) => (
                                      <Input
                                        {...inputField}
                                        id={`search-${index}`}
                                        placeholder="Enter device number"
                                        className="pl-8"
                                        onChange={(e) => {
                                          inputField.onChange(e);
                                          handleDeviceNumberChange(
                                            e.target.value,
                                            index
                                          );
                                        }}
                                      />
                                    )}
                                  />
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Search by device number (e.g., WU-10001)
                                </p>
                              </div>

                              {/* Search Results */}
                              {activeSearchIndex === index &&
                                watchedServices[index]?.deviceNumber && (
                                  <div className="border rounded-md max-h-60 overflow-y-auto">
                                    {currentMeterSearchData?.result?.map(
                                      (meter: any, meterIndex: number) => (
                                        <div
                                          key={meter.id || meterIndex}
                                          className="p-3 cursor-pointer hover:bg-muted/50 border-b last:border-b-0"
                                        >
                                          <div className="flex justify-between items-start">
                                            <div className="space-y-1">
                                              <div className="flex items-center gap-2">
                                                <p className="font-medium">
                                                  {`${meter.deviceNo}`}
                                                </p>
                                              </div>
                                              <p className="text-sm text-muted-foreground">
                                                {meter.meterNumber ||
                                                  "No meter number"}
                                              </p>
                                            </div>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() =>
                                                handleConsumerSelect(
                                                  meter,
                                                  index
                                                )
                                              }
                                            >
                                              Select
                                            </Button>
                                          </div>
                                        </div>
                                      )
                                    )}
                                  </div>
                                )}

                              {/* No results message */}
                              {activeSearchIndex === index &&
                                watchedServices[index]?.deviceNumber?.length >=
                                  3 &&
                                (!currentMeterSearchData?.result ||
                                  currentMeterSearchData.result.length ===
                                    0) && (
                                  <div className="text-center py-4 text-muted-foreground border rounded-md">
                                    <p className="text-sm">
                                      No meters found matching "
                                      {watchedServices[index]?.deviceNumber}"
                                    </p>
                                  </div>
                                )}
                            </div>

                            {/* Enhanced Selected Meter Details Display */}
                            {selectedMeterDetails[index] && (
                              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                                <div className="flex items-center gap-2">
                                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                                  <h4 className="font-medium text-green-800">
                                    Selected Meter Details
                                  </h4>
                                </div>

                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="text-muted-foreground">
                                      Meter ID
                                    </p>
                                    <p className="font-medium text-green-700">
                                      {selectedMeterDetails[index].id}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">
                                      Device Number
                                    </p>
                                    <p className="font-medium">
                                      {selectedMeterDetails[index].deviceNo}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">
                                      Meter Number
                                    </p>
                                    <p className="font-medium">
                                      {selectedMeterDetails[index]
                                        .meterNumber || "N/A"}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">
                                      Status
                                    </p>
                                    <Badge
                                      variant="outline"
                                      className="text-green-800 bg-green-100"
                                    >
                                      {selectedMeterDetails[index]
                                        .statusDisplay || "Available"}
                                    </Badge>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">
                                      Physical Status
                                    </p>
                                    <p className="font-medium">
                                      {selectedMeterDetails[index]
                                        .physicalStatusDisplay || "N/A"}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">
                                      Meter Type
                                    </p>
                                    <p className="font-medium">
                                      {selectedMeterDetails[index]
                                        .meterTypeDisplay || "N/A"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Loading state for meter details */}
                            {loadingMeterDetails[index] && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-center gap-2">
                                  <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                                  <p className="text-blue-700">
                                    Loading meter details...
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Reading inputs */}
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor={`latestReading-${index}`}>
                                  Latest Meter Reading
                                </Label>
                                <Controller
                                  name={`services.${index}.latestReading`}
                                  control={control}
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      id={`latestReading-${index}`}
                                      placeholder="Enter current meter reading"
                                      type="number"
                                    />
                                  )}
                                />
                              </div>
                              <div>
                                <Label htmlFor={`readingDate-${index}`}>
                                  Reading Date
                                </Label>
                                <Controller
                                  name={`services.${index}.readingDate`}
                                  control={control}
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      type="date"
                                      id={`readingDate-${index}`}
                                    />
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    )}
                  </div>

                  <div className="flex justify-between items-center pt-6 border-t border-gray-200 mt-6">
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      type="button"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back
                    </Button>
                    <Button
                      onClick={handleFormArraySubmit}
                      // disabled={
                      //   serviceOrderConsumerData?.result?.statusDisplay !==
                      //     "Approved" ||
                      //   application?.apiStatus.toLowerCase() !==
                      //     "installation inprogress" ||
                      //   updateConsumerInstallationStatusMutation.isPending ||
                      //   application.progressStatusDisplay === "Hold"
                      // }
                      disabled={
                        application?.apiStatus.toLowerCase() !== "installation inprogress" ||
                        serviceOrderConsumerData?.result?.statusDisplay !== "Approved" 
                      }
                    >
                      {updateConsumerInstallationStatusMutation.isPending
                        ? "Updating..."
                        : "Continue to Activation"}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
                )}
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">
                    Something went wrong. Please try again.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Meter Summary Card on the Right Side */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="h-5 w-5 text-workflow-blue" />
                Meter Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="mt-4">
              <div className="space-y-4">
                {/* Show meter mapping data if some objects have meterDetails */}
                {meterMappingData.length > 0 && meterMappingData.some((mapping: any) => mapping.meterDetails) ? (
                  meterMappingData
                    .filter((mapping: any) => mapping.meterDetails)
                    .map((mapping: any, index: number) => (
                    <div
                      key={mapping.consumer || index}
                      className="p-4 rounded-lg border bg-green-50 border-green-100"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Package className="h-4 w-4 text-green-600" />
                        <h4 className="font-medium text-green-800">
                          {mapping.utilityService} Meter
                        </h4>
                      </div>
                      
                        {mapping.meterDetails && (
                          <div className="grid grid-cols-2 gap-6">
                            {/* Left Column - Basic Info */}
                      <div className="text-sm space-y-2">
                        <div className="flex items-center">
                          <CheckCircle2 className="h-3 w-3 mr-2 text-green-500" />
                          <span className="font-medium">
                                  {mapping.meterDetails.meterNumber}
                          </span>
                        </div>
                              <p>Device: {mapping.meterDetails.deviceNo}</p>
                        <p>
                          Meter ID:{" "}
                          <span className="text-green-600 font-medium">
                                  {mapping.meterDetails.id}
                          </span>
                        </p>
                        <p>
                                Type:{" "}
                          <span className="text-green-600 font-medium">
                                  {mapping.meterDetails.meterTypeDisplay}
                          </span>
                        </p>
                      </div>
                            
                            {/* Right Column - Status & Readings */}
                            <div className="text-sm space-y-2">
                              <p>
                                Physical Status:{" "}
                                <Badge 
                                  variant="outline" 
                                  className={
                                    mapping.meterDetails.physicalStatusDisplay === "Installed" 
                                      ? "text-green-800 bg-green-100" 
                                      : "text-yellow-800 bg-yellow-100"
                                  }
                                >
                                  {mapping.meterDetails.physicalStatusDisplay}
                                </Badge>
                              </p>
                              <p>
                                Status:{" "}
                                <Badge 
                                  variant="outline" 
                                  className="text-blue-800 bg-blue-100"
                                >
                                  {mapping.meterDetails.statusDisplay}
                                </Badge>
                              </p>
                              <p>
                                Current Reading:{" "}
                                <span className="font-medium">
                                  {mapping.meterDetails.currentReading}
                                </span>
                              </p>
                              <p>
                                Reading Date:{" "}
                                <span className="font-medium">
                                  {mapping.meterDetails.currentReadingDate}
                                </span>
                              </p>
                              <p>
                                Route:{" "}
                                <span className="font-medium">
                                  {mapping.meterDetails.routeName}
                                </span>
                              </p>
                            </div>
                          </div>
                    )}
                  </div>
                  ))
                ) : (
                  /* Show "Meter is still not assigned" message when no mapping data */
                  <div className="p-4 rounded-lg border bg-yellow-50 border-yellow-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-4 w-4 text-yellow-600" />
                      <h4 className="font-medium text-yellow-800">Meter Status</h4>
                    </div>
                    <p className="text-sm text-yellow-700">
                      Meter is still not assigned
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default InstallationPage;
